import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Badge,
  Flex,
  Grid,
  GridItem,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Button,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  Textarea,
  Radio,
  RadioGroup,
  Stack,
  useDisclosure,
  useToast,
  Alert,
  AlertIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Checkbox,
  CheckboxGroup,
  Divider,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import {
  MdSearch,
  MdTrendingUp,
  MdPeople,
  MdCheckCircle,
  MdCancel,
  MdAdd,
  MdPlayArrow,
} from 'react-icons/md';
import {
  getAllSalaryIncreases,
  createSalaryIncrease,
  applySalaryIncrease,
  cancelSalaryIncrease,
  addEmployeesToIncrease,
  getSalaryIncreaseById,
} from 'services/salary.service';
import httpClient from '../../../utils/httpClient';

const STATUS_MAP = {
  PENDING: { label: 'Pendiente', color: 'yellow' },
  APPLIED: { label: 'Aplicado', color: 'green' },
  CANCELLED: { label: 'Cancelado', color: 'red' },
};

const TYPE_MAP = {
  GENERAL: 'General',
  INDIVIDUAL: 'Individual',
};

export default function SalaryIncreasesView() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');
  const cardBg = useColorModeValue('white', 'navy.800');
  const toast = useToast();

  const [increases, setIncreases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
  const [detailData, setDetailData] = useState(null);
  const [form, setForm] = useState({
    increaseType: 'GENERAL',
    percentage: '',
    fixedAmount: '',
    reason: '',
    effectiveDate: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      const res = await getAllSalaryIncreases(filters);
      setIncreases(res?.payload?.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await httpClient.get('/employee');
      const emps = res?.payload || res?.data?.payload || [];
      setEmployees(Array.isArray(emps) ? emps : []);
    } catch (error) {
      console.error('Error cargando empleados:', error);
    }
  };

  const handleOpenCreate = () => {
    setForm({
      increaseType: 'GENERAL',
      percentage: '',
      fixedAmount: '',
      reason: '',
      effectiveDate: '',
      notes: '',
    });
    setSelectedEmployees([]);
    loadEmployees();
    onCreateOpen();
  };

  const handleCreate = async () => {
    if (!form.reason || !form.effectiveDate || (!form.percentage && !form.fixedAmount)) {
      toast({ title: 'Completá los campos obligatorios', status: 'warning', duration: 3000 });
      return;
    }

    if (form.increaseType === 'INDIVIDUAL' && selectedEmployees.length === 0) {
      toast({ title: 'Seleccioná al menos un empleado', status: 'warning', duration: 3000 });
      return;
    }

    try {
      setSaving(true);
      const res = await createSalaryIncrease({
        increaseType: form.increaseType,
        percentage: form.percentage ? Number(form.percentage) : 0,
        fixedAmount: form.fixedAmount ? Number(form.fixedAmount) : null,
        reason: form.reason,
        effectiveDate: form.effectiveDate,
        notes: form.notes,
        createdBy: 1,
      });

      if (res?.status === 201 && form.increaseType === 'INDIVIDUAL') {
        await addEmployeesToIncrease(res.payload.id, selectedEmployees.map(Number));
      }

      toast({ title: 'Aumento creado correctamente', status: 'success', duration: 3000 });
      onCreateClose();
      loadData();
    } catch (error) {
      toast({ title: 'Error al crear aumento', status: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const handleApply = async (id) => {
    if (!window.confirm('¿Confirmar aplicación del aumento? Esta acción actualizará los salarios de los empleados.')) return;
    try {
      const res = await applySalaryIncrease(id);
      toast({ title: res?.message || 'Aumento aplicado', status: 'success', duration: 3000 });
      loadData();
    } catch (error) {
      toast({ title: error?.response?.data?.message || 'Error', status: 'error', duration: 3000 });
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('¿Cancelar este aumento?')) return;
    try {
      await cancelSalaryIncrease(id);
      toast({ title: 'Aumento cancelado', status: 'success', duration: 3000 });
      loadData();
    } catch (error) {
      toast({ title: 'Error', status: 'error', duration: 3000 });
    }
  };

  const handleViewDetail = async (id) => {
    try {
      const res = await getSalaryIncreaseById(id);
      setDetailData(res?.payload);
      onDetailOpen();
    } catch (error) {
      toast({ title: 'Error al cargar detalle', status: 'error', duration: 3000 });
    }
  };

  const filtered = useMemo(() => {
    return increases.filter((inc) => {
      const term = searchTerm.toLowerCase();
      return (
        inc.reason?.toLowerCase().includes(term) ||
        inc.increase_type?.toLowerCase().includes(term)
      );
    });
  }, [increases, searchTerm]);

  const stats = useMemo(() => {
    const pending = increases.filter((i) => i.status === 'PENDING').length;
    const applied = increases.filter((i) => i.status === 'APPLIED').length;
    const totalEmployees = increases.reduce((sum, i) => sum + Number(i.employee_count || 0), 0);
    return { pending, applied, totalEmployees };
  }, [increases]);

  if (loading) return null;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} mb={6}>
        <GridItem>
          <Card bg={cardBg} p={4}>
            <Stat>
              <StatLabel color="gray.500">Pendientes</StatLabel>
              <StatNumber color="orange.500">{stats.pending}</StatNumber>
              <StatHelpText>Por aplicar</StatHelpText>
            </Stat>
          </Card>
        </GridItem>
        <GridItem>
          <Card bg={cardBg} p={4}>
            <Stat>
              <StatLabel color="gray.500">Aplicados</StatLabel>
              <StatNumber color="green.500">{stats.applied}</StatNumber>
              <StatHelpText>Aumentos efectivizados</StatHelpText>
            </Stat>
          </Card>
        </GridItem>
        <GridItem>
          <Card bg={cardBg} p={4}>
            <Stat>
              <StatLabel color="gray.500">Empleados afectados</StatLabel>
              <StatNumber color="blue.500">{stats.totalEmployees}</StatNumber>
              <StatHelpText>En total</StatHelpText>
            </Stat>
          </Card>
        </GridItem>
      </Grid>

      <Card p={4}>
        <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
          <Flex gap={3} align="center" flexWrap="wrap">
            <InputGroup w="280px">
              <InputLeftElement>
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Buscar por motivo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Select w="180px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Todos</option>
              <option value="PENDING">Pendientes</option>
              <option value="APPLIED">Aplicados</option>
              <option value="CANCELLED">Cancelados</option>
            </Select>
          </Flex>
          <Button leftIcon={<MdAdd />} colorScheme="brand" onClick={handleOpenCreate}>
            Nuevo aumento
          </Button>
        </Flex>

        {filtered.length === 0 ? (
          <Flex justify="center" py={10}>
            <Text color="gray.500">No hay aumentos registrados</Text>
          </Flex>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th borderColor={borderColor}>Tipo</Th>
                  <Th borderColor={borderColor}>Motivo</Th>
                  <Th borderColor={borderColor}>%</Th>
                  <Th borderColor={borderColor}>Monto fijo</Th>
                  <Th borderColor={borderColor}>Fecha vigencia</Th>
                  <Th borderColor={borderColor}>Empleados</Th>
                  <Th borderColor={borderColor}>Estado</Th>
                  <Th borderColor={borderColor}>Acciones</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filtered.map((inc) => {
                  const st = STATUS_MAP[inc.status] || { label: inc.status, color: 'gray' };
                  return (
                    <Tr key={inc.id} _hover={{ bg: rowHover }} cursor="pointer" onClick={() => handleViewDetail(inc.id)}>
                      <Td borderColor={borderColor}>
                        <Badge colorScheme={inc.increase_type === 'GENERAL' ? 'blue' : 'purple'}>
                          {TYPE_MAP[inc.increase_type] || inc.increase_type}
                        </Badge>
                      </Td>
                      <Td borderColor={borderColor}>
                        <Text color={textColor} fontWeight="600" fontSize="sm">
                          {inc.reason}
                        </Text>
                      </Td>
                      <Td borderColor={borderColor}>
                        <Text fontWeight="600">{Number(inc.percentage).toFixed(2)}%</Text>
                      </Td>
                      <Td borderColor={borderColor}>
                        {inc.fixed_amount ? `$${Number(inc.fixed_amount).toLocaleString()}` : '-'}
                      </Td>
                      <Td borderColor={borderColor}>
                        {new Date(inc.effective_date).toLocaleDateString('es-UY')}
                      </Td>
                      <Td borderColor={borderColor}>
                        <Badge>{inc.employee_count}</Badge>
                      </Td>
                      <Td borderColor={borderColor}>
                        <Badge colorScheme={st.color}>{st.label}</Badge>
                      </Td>
                      <Td borderColor={borderColor} onClick={(e) => e.stopPropagation()}>
                        <HStack spacing={1}>
                          {inc.status === 'PENDING' && (
                            <>
                              <Button
                                size="xs"
                                colorScheme="green"
                                leftIcon={<MdPlayArrow />}
                                onClick={() => handleApply(inc.id)}
                              >
                                Aplicar
                              </Button>
                              <Button
                                size="xs"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => handleCancel(inc.id)}
                              >
                                Cancelar
                              </Button>
                            </>
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </Card>

      {/* Modal crear aumento */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Nuevo aumento salarial</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl mb={4}>
              <FormLabel>Tipo de aumento</FormLabel>
              <RadioGroup value={form.increaseType} onChange={(v) => setForm({ ...form, increaseType: v })}>
                <Stack direction="row" spacing={6}>
                  <Radio value="GENERAL">General (todos los empleados)</Radio>
                  <Radio value="INDIVIDUAL">Individual</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            <Grid templateColumns="1fr 1fr" gap={4} mb={4}>
              <FormControl>
                <FormLabel>Porcentaje (%)</FormLabel>
                <NumberInput min={0} max={100} precision={2}>
                  <NumberInputField
                    placeholder="Ej: 8.50"
                    value={form.percentage}
                    onChange={(e) => setForm({ ...form, percentage: e.target.value })}
                  />
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel>Monto fijo (alternativo)</FormLabel>
                <NumberInput min={0}>
                  <NumberInputField
                    placeholder="Ej: 5000"
                    value={form.fixedAmount}
                    onChange={(e) => setForm({ ...form, fixedAmount: e.target.value })}
                  />
                </NumberInput>
              </FormControl>
            </Grid>

            <FormControl mb={4} isRequired>
              <FormLabel>Motivo</FormLabel>
              <Input
                placeholder="Ej: Consejo de Salarios - Julio 2026"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </FormControl>

            <FormControl mb={4} isRequired>
              <FormLabel>Fecha de vigencia</FormLabel>
              <Input
                type="date"
                value={form.effectiveDate}
                onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })}
              />
            </FormControl>

            <FormControl mb={4}>
              <FormLabel>Notas</FormLabel>
              <Textarea
                placeholder="Observaciones opcionales..."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
              />
            </FormControl>

            {form.increaseType === 'INDIVIDUAL' && (
              <>
                <Divider my={4} />
                <Text fontWeight="600" mb={2}>Seleccionar empleados</Text>
                {employees.length === 0 ? null : (
                  <Box maxH="200px" overflowY="auto" border="1px solid" borderColor={borderColor} borderRadius="md" p={2}>
                    <CheckboxGroup value={selectedEmployees} onChange={setSelectedEmployees}>
                      <Stack spacing={1}>
                        {employees.map((emp) => {
                          const name = emp.person
                            ? `${emp.person.name} ${emp.person.lastName || emp.person.last_name || ''}`
                            : `Empleado #${emp.id}`;
                          return (
                            <Checkbox key={emp.id} value={String(emp.id)}>
                              {name}
                            </Checkbox>
                          );
                        })}
                      </Stack>
                    </CheckboxGroup>
                  </Box>
                )}
              </>
            )}

            {form.increaseType === 'GENERAL' && (
              <Alert status="info" borderRadius="md" mt={3}>
                <AlertIcon />
                El aumento general se aplicará a todos los empleados activos al momento de ejecutarlo.
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onCreateClose}>Cancelar</Button>
            <Button colorScheme="brand" onClick={handleCreate}>
              Crear aumento
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal detalle */}
      <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Detalle del aumento</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {detailData ? (
              <>
                <Grid templateColumns="1fr 1fr" gap={3} mb={4}>
                  <Box>
                    <Text fontSize="sm" color="gray.500">Tipo</Text>
                    <Badge colorScheme={detailData.increase?.increase_type === 'GENERAL' ? 'blue' : 'purple'}>
                      {TYPE_MAP[detailData.increase?.increase_type]}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500">Estado</Text>
                    <Badge colorScheme={STATUS_MAP[detailData.increase?.status]?.color}>
                      {STATUS_MAP[detailData.increase?.status]?.label}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500">Porcentaje</Text>
                    <Text fontWeight="600">{Number(detailData.increase?.percentage).toFixed(2)}%</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500">Fecha vigencia</Text>
                    <Text fontWeight="600">
                      {new Date(detailData.increase?.effective_date).toLocaleDateString('es-UY')}
                    </Text>
                  </Box>
                  <Box gridColumn="span 2">
                    <Text fontSize="sm" color="gray.500">Motivo</Text>
                    <Text fontWeight="600">{detailData.increase?.reason}</Text>
                  </Box>
                </Grid>

                {detailData.details && detailData.details.length > 0 && (
                  <>
                    <Divider my={3} />
                    <Text fontWeight="600" mb={2}>Empleados ({detailData.details.length})</Text>
                    <Box overflowX="auto">
                      <Table size="sm" variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Empleado</Th>
                            <Th isNumeric>Salario anterior</Th>
                            <Th isNumeric>Salario nuevo</Th>
                            <Th isNumeric>Monto</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {detailData.details.map((d) => (
                            <Tr key={d.id}>
                              <Td>{d.employee_name}</Td>
                              <Td isNumeric>${Number(d.previous_salary).toLocaleString()}</Td>
                              <Td isNumeric fontWeight="600" color="green.500">
                                ${Number(d.new_salary).toLocaleString()}
                              </Td>
                              <Td isNumeric>+${Number(d.actual_amount).toLocaleString()}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </>
                )}
              </>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onDetailClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
